import { useEffect, useRef, useState } from 'react';
import type { AuthSession, UserRole } from '@builder/shared';
import { renderGoogleButton } from '@/lib/google-gis';
import { api, ApiError } from '@/api/client';
import { useTheme } from '@/hooks/useTheme';
import { useT } from '@/hooks/useT';
import { Spinner } from '@/components/ui/Spinner';

/**
 * Google "Continue with Google" button.
 *
 * Renders the official GIS button into a host div — we can't visually fork
 * Google's button because the click-target must live inside their iframe —
 * but we match our theme (light/dark) and locale so it blends in. The host
 * is sized to match our primary Button (h-11) for visual parity with the
 * form below it.
 *
 * The component is self-contained: it calls the API, stores the session via
 * the parent's callback, and surfaces errors to the parent via `onError`.
 */
interface Props {
  /** Set to the role the user picked on the register page. Ignored for existing users. */
  role?: UserRole;
  /** Fired with the session after successful Google sign-in. */
  onSuccess: (session: AuthSession) => void;
  /**
   * Fired with a user-facing error message AND the API error code (when known)
   * so the parent can route `pending_approval` to a calm warn banner instead
   * of treating every failure the same way. Code is `null` when the failure
   * happened in the GIS layer (no API roundtrip).
   */
  onError: (message: string, code?: string | null) => void;
}

export function GoogleSignInButton({ role, onSuccess, onError }: Props) {
  const { t, locale } = useT();
  const { resolved } = useTheme();
  const hostRef = useRef<HTMLDivElement>(null);
  const clientId = (import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '') as string;
  const [busy, setBusy] = useState(false);
  const [booting, setBooting] = useState(true);

  // Keep the latest callbacks / role in refs so we don't re-render the
  // Google button every time the parent re-renders. The button only needs
  // to be re-mounted when clientId, theme, or locale changes.
  const roleRef = useRef(role);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    roleRef.current = role;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [role, onSuccess, onError]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !clientId) {
      setBooting(false);
      return;
    }

    let cancelled = false;
    let cleanup: (() => void) | null = null;
    setBooting(true);

    renderGoogleButton({
      host,
      clientId,
      locale,
      options: {
        theme: resolved === 'dark' ? 'filled_black' : 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
        width: Math.min(host.clientWidth || 320, 400),
      },
      onCredential: async (idToken: string) => {
        if (cancelled) return;
        setBusy(true);
        try {
          const session = await api.loginWithGoogle({ idToken, role: roleRef.current });
          onSuccessRef.current(session);
        } catch (err) {
          const message =
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : t.auth.googleSignInFailed;
          // Wave 320b — preserve the API error code so the parent can route
          // `pending_approval` to a calm warn banner instead of a red error.
          // Without this, a brand-new Google signup landed on the same red
          // strip as a real failure, which JP read as "white screen / didn't
          // work" because the message also doesn't say "you've been approved
          // already, you can come back."
          const code = err instanceof ApiError ? err.code : null;
          onErrorRef.current(message, code);
        } finally {
          if (!cancelled) setBusy(false);
        }
      },
      onError: (err) => {
        if (cancelled) return;
        onErrorRef.current(err.message || t.auth.googleSignInFailed, null);
      },
    })
      .then((dispose) => {
        if (cancelled) dispose();
        else cleanup = dispose;
      })
      .finally(() => {
        if (!cancelled) setBooting(false);
      });

    return () => {
      cancelled = true;
      cleanup?.();
    };
    // Intentionally excluding the callback refs — they're pinned via roleRef/onSuccessRef/onErrorRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, resolved, locale]);

  if (!clientId) {
    // Silent when unconfigured — the parent page falls back to email/password
    // without showing a broken Google button to real users.
    return null;
  }

  return (
    <div className="relative">
      <div
        ref={hostRef}
        aria-label={t.auth.continueWithGoogle}
        className="flex min-h-[44px] items-center justify-center"
      />
      {(booting || busy) && (
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-surface-2/80 backdrop-blur-sm"
          aria-hidden="true"
        >
          <Spinner className="h-4 w-4" />
        </div>
      )}
    </div>
  );
}
